/**
 * Missions Direct API Client
 *
 * API client for the gw-missions service (missions.{env}.generalwisdom.com).
 * This is the live path used when `missionsFixtureMode` is false.
 *
 * Extends BaseApiClient for authentication, error handling, and type-safe
 * requests. The base URL is configurable via `missionsApiBaseUrl` in
 * runtimeConfig — falls back to `apiBaseUrl` when not set.
 */

import { getConfig } from '@/config/runtimeConfig';

import { BaseApiClient } from './baseApiClient';

import type { AgentActivityEvent, MissionGraph, SubjectIdentificationSectionProps } from '@gw/ui';
import type { MissionBreadth, MissionCostStats, Run } from '@gw/ui/types';

// GW-12916: SEC-051's `fetchStaticMap` contract types (`FetchStaticMap` /
// `StaticMapRequest`) live in gw-ui's LocationMiniMap molecule but are NOT
// re-exported from the `@gw/ui` package root or `@gw/ui/types`. A deep
// `@gw/ui/dist/...` import is prohibited (see gw-spa CLAUDE.md), so derive them
// from the root-exported `SubjectIdentificationSectionProps` instead — the same
// public prop the host threads the fetcher into. Single derivation site;
// `useFetchStaticMap` re-imports `FetchStaticMap` from here.
export type FetchStaticMap = NonNullable<SubjectIdentificationSectionProps['fetchStaticMap']>;
export type StaticMapRequest = Parameters<FetchStaticMap>[0];

// ============================================================================
// Response Shapes
// ============================================================================

/**
 * A single mission record as returned by GET /v1/missions.
 * These fields are the minimum contract established in the Pact spec.
 */
export interface MissionListItem {
  missionId: string;
  missionType: string;
  status: string;
  /**
   * GW-10160: `Mission.prompt` is a required field on every mission per the
   * deployed missions.dev.generalwisdom.com/v1/openapi.yaml Mission schema —
   * GET /v1/missions returns full Mission objects (toWireMission embeds
   * models.Mission verbatim, only Target is overridden), not a narrower
   * list-item DTO. This field was missing here even though the real wire
   * response always carries it.
   */
  prompt: string;
  target: {
    name: string;
    type?: string;
  };
  createdAt: string;
  updatedAt?: string;
  shortName?: string;
  displayName?: string;
  tokensConsumed?: number | null;
  tokensUsed?: number | null;
  requiresHitl?: boolean;
  /**
   * GW-10160: the real Mission schema has no `runCount`/`latestRunId` fields
   * — only `runIds: string[]` (every run ever created for this mission,
   * oldest first). Kept as optional since `toWireMission` omits it when
   * empty (`json:"runIds,omitempty"`).
   */
  runIds?: string[];
  runCount?: number;
  latestRunId?: string | null;
  productCount?: number;
  /**
   * GW-11906 (Epic GW-11903, Wave-3): aggregate settled SMART-token spend
   * across every run of this mission. GET /v1/missions now emits it per
   * mission (gw-missions-api Wave-2, GW-11905) — the same
   * `totalCostAllRunsTokens` field the single-mission `Mission` schema below
   * already carries. Drives the missions list "Total Cost" column via
   * `adaptToMissionSummary`; absent/0 until at least one run of this mission
   * has settled.
   */
  totalCostAllRunsTokens?: number;
}

interface ListMissionsResponse {
  missions: MissionListItem[];
  nextCursor: string | null;
  /**
   * GW-10231: total count of missions matching this scope, independent of
   * limit/cursor/offset — always present per the deployed
   * missions.dev.generalwisdom.com/v1/openapi.yaml MissionListResponse
   * schema (`required: [missions, total]`). Used by
   * `missionsSlice.fetchMissionsForScope` to know when it has fetched every
   * page for a scope.
   */
  total: number;
}

/**
 * A reference to a mission deliverable product, used to populate
 * MarketplaceItem.products. Mirrors gw-go-common's models.ProductSummary
 * verbatim (GW-8625; enabled/autoInclude/role/description added GW-10558) —
 * camelCase field names over the wire.
 *
 * The server resolves enabled/autoInclude/role from
 * gw-missions-api's product-overrides.json (default enabled=true/
 * autoInclude=false/role='mission' for any id not explicitly listed) and
 * OMITS any product whose resolved `enabled` is false from the response
 * entirely — this type's `enabled` field is always true in practice, kept
 * for completeness/future use, and must not drive additional client-side
 * filtering (GW-10559/GW-10560).
 */
export interface ProductSummary {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  autoInclude: boolean;
  role: 'mission' | 'core';
}

export interface MarketplaceItem {
  itemId: string;
  itemType: string;
  name: string;
  status: string;
  visibility: string;
  slug?: string;
  description?: string;
  keywords?: string[];
  tags?: string[];
  iconUrl?: string;
  publisherName?: string;
  stepCount?: number | null;
  taxonomyRef?: { taxonomyId: string; taxonomyVersion: string } | null;
  /**
   * GW-8601: latest hourly cost-stats job snapshot for this mission type —
   * mirrors gw-missions-api's OpenAPI MissionCostStats schema
   * (openapi/missions-api.yaml) and gw-go-common's models.MissionCostStats
   * verbatim (missionType/minTokens/averageTokens/maxTokens/sampleCount/
   * computedAt). null until the hourly job has produced at least one
   * snapshot for this mission type.
   */
  costStats?: MissionCostStats | null;
  /**
   * GW-8122: derived pricing band mirroring gw-missions-api's
   * MarketplacePricing schema — null until the first hourly cost-stats
   * snapshot exists for this mission type.
   */
  pricing?: {
    minTokens: number;
    averageTokens: number;
    maxTokens: number;
    sampleCount: number;
  } | null;
  /**
   * GW-8625/GW-8627 (PRP-GW8581-02 FR-5/FR-7): the deliverable products this
   * mission template produces, resolved from TaxonomyMission.productIds
   * against gw-missions-api's embedded product taxonomy. itemType=MISSION
   * only; absent/null for every other itemType, and for a MISSION template
   * with no productIds — never a fabricated empty array in that case.
   */
  products?: ProductSummary[] | null;
  /**
   * GW-10568 (Epic GW-10563; v2 GW-10640): per-mission graph the marketplace
   * item response carries (gw-missions-api GW-10565/GW-10637), mirroring @gw/ui's
   * `MissionGraph` shape. v2 (mission-graph-v2-accurate-directed-contract) makes
   * it accurate and directed: nodes carry a `kind` (`step`/`product`) and edges
   * are directed, typed `{from,to,kind}` objects (`dataflow`/`product`/`feedback`)
   * — the old `[i,j]` int-tuples with the `i < j` invariant are gone. Threaded
   * into MissionCatalogCard's `graph` prop so Browse renders the faithful
   * phase-colored graph figure; null/absent until the mission's graph is
   * compiled, in which case the card falls back to the img/fingerprint path.
   */
  graph?: MissionGraph | null;
  /**
   * GW-11450 (merged): gw-missions-api's `MarketplaceItem` schema
   * (openapi/missions-api.yaml) never gained `tosRequiredLevel`/
   * `tosContent`/`tosVersion` fields — a mission's TOS requirement/content
   * is exposed only via `GET /v1/missions/{itemId}/tos/acceptance`
   * (`MissionTOSAcceptanceStatus` below), not denormalized onto the listing
   * item. These optional fields are kept only because some display call
   * sites still probe them defensively; useMissionTOS.ts itself no longer
   * reads them — it always calls the `/tos/acceptance` endpoint directly
   * and derives requirement/version from that response.
   */
  tosRequiredLevel?: MissionTOSRequiredLevel;
  tosContent?: string;
  tosVersion?: number;
  tosVersionLabel?: string;
  tosUpdatedAt?: string;
}

interface ListMarketplaceMissionsResponse {
  items: MarketplaceItem[];
  nextCursor?: string | null;
}

// ============================================================================
// TOS Types (GW-11455, PRP-MKTPL-016 FR-6/FR-7/FR-23)
// ============================================================================

/**
 * Mirrors gw-ui's `TOSRequiredType` shape used by applications/datasets, but
 * defined locally here since mission listing fields (unlike Application/
 * Dataset) are not yet part of `@gw/ui/types` — `MarketplaceItem` itself is
 * a gw-spa-local type (see above), and this repo owns it until/unless a
 * future issue promotes mission listing types into `@gw/ui`.
 */
export type MissionTOSRequiredLevel = 'none' | 'user' | 'org' | 'both';

/**
 * A single user- or org-level acceptance record, embedded in
 * `MissionTOSAcceptanceStatus`. Mirrors the identical shape used by
 * applications/datasets/service-offerings (`TOSAcceptanceRecord` in each
 * backend) — ground truth confirmed against gw-missions-api's
 * `functions/missions-tos/main.go` (`tosAcceptanceRecord`) and the Pact
 * consumer contract it implements verbatim
 * (`pact/consumers/missions-tos.pact.spec.ts`).
 */
export interface MissionTOSAcceptanceDetail {
  accepted: boolean;
  acceptanceType: 'user' | 'org';
  acceptedVersion: number;
  acceptedAt: string;
}

/**
 * GET /v1/missions/{itemId}/tos/acceptance response — confirmed against
 * gw-missions-api's `functions/missions-tos/main.go`
 * (`tosAcceptanceStatusResponse`) and its Pact consumer contract
 * (`pact/consumers/missions-tos.pact.spec.ts`), the same canonical shape
 * shared by applications/datasets/service-offerings.
 */
export interface MissionTOSAcceptanceStatus {
  itemId: string;
  tosRequiredLevel: MissionTOSRequiredLevel;
  tosVersion: number | null;
  userAcceptance: MissionTOSAcceptanceDetail | null;
  orgAcceptance: MissionTOSAcceptanceDetail | null;
  userNeedsReaccept: boolean;
  orgNeedsReaccept: boolean;
  canProceed: boolean;
}

/**
 * POST /v1/missions/{itemId}/tos/accept/{user|org} request body.
 */
export interface AcceptMissionTOSRequest {
  tosVersion: number;
}

/**
 * POST /v1/missions/{itemId}/tos/accept/{user|org} response — confirmed
 * against gw-missions-api's `tosAcceptResponse` and its Pact consumer
 * contract; the same shape applications/datasets/service-offerings return
 * (acceptanceType/tosVersion/acceptedAt, no id/missionItemId/userId/orgId
 * envelope).
 */
export interface MissionTOSAcceptance {
  acceptanceType: 'user' | 'org';
  tosVersion: number;
  acceptedAt: string;
}

/**
 * GW-11493: FR-29 cross-mission org TOS overview.
 * GET /v1/missions/tos/org-overview (org_admin/global_admin only).
 * Mirrors gw-missions-api's `tosOrgOverviewResponse`
 * (`functions/missions-tos/main.go`) — the same summary/items envelope as
 * applications' `ApplicationOrgTOSOverviewResponse`, but with
 * itemId/itemName rather than appId/appName/category/iconUrl.
 */
export interface MissionOrgTOSOverviewResponse {
  items: Array<{
    itemId: string;
    itemName: string;
    tosRequiredLevel: MissionTOSRequiredLevel;
    tosVersion: number | null;
    orgAccepted: boolean;
    orgNeedsReaccept: boolean;
  }>;
  summary: {
    total: number;
    accepted: number;
    pending: number;
    needsReaccept: number;
  };
}

/**
 * GET /v1/missions/{missionId}/runs response — mirrors gw-missions-api's
 * `RunListResponse` schema (openapi/missions-api.yaml) verbatim.
 */
interface ListRunsResponse {
  runs: Run[];
}

/**
 * GW-8762: the mission's investigation subject, as persisted on the Mission
 * record — mirrors gw-missions-api's OpenAPI `Subject` schema
 * (openapi/missions-api.yaml) verbatim. All fields optional on the wire; the
 * mapper in searchToMissionBriefing.adapter.ts treats every one as
 * genuinely absent rather than defaulting them.
 */
export interface MissionApiSubject {
  type?: string;
  name?: string;
  canonicalId?: string;
  attributes?: Record<string, unknown>;
}

/**
 * GET /v1/missions/{missionId} response — mirrors gw-missions-api's
 * `Mission` schema (openapi/missions-api.yaml) verbatim. GW-8762: this is
 * the real mission record MissionRunPage's header needs (prompt/target/
 * breadth) instead of the fabricated-empty `buildMinimalBriefing`.
 *
 * GW-10830: the reconciled Mission schema (GW-10314) dropped the top-level
 * `depthTier` field and stores the investigation `breadth` verbatim
 * (`basic|extended|complete`), mirroring `MissionBriefing.breadth` set once
 * at mission creation — so this reads `breadth` directly, no depthTier→breadth
 * mapping.
 */
export interface Mission {
  missionId: string;
  userId: string;
  orgId: string;
  missionType: string;
  status: string;
  prompt: string;
  target: MissionApiSubject;
  breadth: MissionBreadth;
  marketplaceTemplateId?: string;
  createdAt: string;
  updatedAt: string;
  runIds?: string[];
  latestReportProductS3Path?: string | null;
  totalCostAllRunsTokens?: number;
}

/**
 * GW-12329: one predicate a subject was expected to resolve but did not this
 * run — mirrors gw-missions-api's OpenAPI `PredicateGap` schema (which itself
 * mirrors gw-mission-engine's `PredicateGap` model). The REAL runtime shape of
 * `missingPredicates` is an array of THESE objects, not `string[]` — the
 * string[]-vs-object[] drift is exactly what surfaced as provenance rendering
 * "[object Object]" (GW-12262). The adapter normalizes these to display
 * strings for gw-ui's `SubjectIdentificationSection` (which takes `string[]`).
 */
export interface PredicateGap {
  predicate: string;
  /** Closed enum of gap reasons (gw-mission-engine PredicateGapReason). */
  reasonCode:
    | 'entity_type_unmapped'
    | 'no_provider_configured'
    | 'provider_status_preview'
    | 'binding_absent'
    | 'dataset_returned_no_match'
    | 'not_collected_this_run'
    | 'dataset_unavailable'
    | 'credential_missing'
    | 'schema_unresolved'
    | 'referenced_content_fetch_failed'
    | 'instruction_missing'
    | 'analyst_only'
    | 'contract_lookup_failed';
  /** Optional human-readable detail for the gap. */
  detail?: string | null;
}

/**
 * GW-12329: one provenance record for a resolved predicate value — mirrors
 * gw-missions-api's OpenAPI `PredicateProvenance` schema (which mirrors
 * gw-mission-engine's `PredicateProvenance` model, all keys camelCase). The
 * engine persists this per predicate (GW-12319); it backs the resolved value's
 * source trail rather than being fabricated by the SPA.
 */
export interface PredicateProvenance {
  sourceType: 'dataset' | 'briefing';
  datasetId?: string;
  queryId?: string;
  resolutionStep?: string;
  confidence?: number | null;
  evidenceIds?: string[];
  runId?: string;
  queryIds?: string[];
  source?: string;
  /**
   * GW-12491 §3.1: the audit-event ids (PROD-CORE-003) that back THIS record's
   * value, derived by the engine from the record's cited query ids. A subset of
   * the section envelope's `auditTrailIds[]`. `subjectProfileAdapter` unions
   * these per predicate and maps them onto gw-ui's `predicateCitations` inline
   * marker prop. `status: planned` on the wire until the engine emits it, so
   * absent renders no marker (no-op-safe).
   */
  auditTrailIds?: string[];
}

/**
 * GW-9864: one SEC-051 (Subject Identification) per-subject element inside a
 * run product's section — mirrors gw-missions-api's OpenAPI
 * `SubjectProfileSubjectElement` schema (`openapi/missions-api.yaml`) and
 * `handler/products.go`'s Go struct verbatim, camelCase JSON tags. This is the
 * SAME shape `SubjectIdentificationSection` (gw-ui) consumes per subject — no
 * adapter re-mapping is needed beyond defaulting absent optional fields and
 * normalizing `missingPredicates`/`predicateAttributes` (see
 * `toSubjectIdentificationSections`).
 *
 * GW-12329: `missingPredicates` is the real `PredicateGap[]` object shape (a
 * bare `string[]` is still accepted for the older writer path); `provenance`
 * is the real per-predicate `PredicateProvenance[]` map.
 */
export interface RunProductSubjectElement {
  entityCanonicalId: string;
  fullName: string;
  entityType: string;
  entitySubtype?: string;
  aliases?: string[];
  knownIdentifiers?: Record<string, string>;
  predicateAttributes?: Record<string, unknown>;
  requiredPredicates?: string[];
  recommendedPredicates?: string[];
  missingPredicates?: Array<string | PredicateGap>;
  disambiguationStatement?: string;
  provenance?: Record<string, PredicateProvenance[]>;
}

/**
 * GW-9864: one assembled section of a run product — mirrors
 * gw-missions-api's OpenAPI `SubjectProfileSection` schema verbatim.
 *
 * GW-10693: two real, currently-possible producer shapes exist for the same
 * productId/sectionId, depending on which gw-mission-engine pipeline wrote
 * it — both are first-class, not one canonical + one fallback:
 *   - `elements` (plural): the older `assemble_product.py` writer path.
 *   - `element` (singular key, array value): the current primary production
 *     writer (`ProductionAgent` -> `create_section.py`/`create_product.py`),
 *     the live dispatch target for OSINT-MISSION-29 today. Only the JSON
 *     KEY is singular — the value is still an array, one entry per subject.
 *
 * GW-12329: the section now carries the per-section provenance "envelope" the
 * engine computes (GW-12319): the NATO Admiralty `admiraltyReliability` (A–F)
 * / `admiraltyCredibility` (1–6) grades, `sourcesCited`, `dataGaps`, and
 * `auditTrailIds`. The SPA must render these real values — never fabricate an
 * `F`/`6` placeholder (see `resolveSectionEnvelope` in subjectProfileAdapter).
 */
export interface RunProductSection {
  sectionId: string;
  admiraltyReliability?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
  admiraltyCredibility?: number;
  sourcesCited?: string[];
  dataGaps?: string[];
  /**
   * Ids of the audit-trail events backing this section, re-populated at
   * finalization (GW-12262). Consumed by the SP page's section-level
   * provenance view (NOT the separate PROD-CORE-003 Audit Trail product).
   */
  auditTrailIds?: string[];
  elements?: RunProductSubjectElement[];
  element?: RunProductSubjectElement[];
}

/**
 * GW-9864: `ProductMeta` — mirrors gw-missions-api's OpenAPI schema verbatim.
 */
export interface RunProductMeta {
  getPath: string;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
}

/**
 * GET /v1/missions/{missionId}/runs/{runId}/products/{productId} response —
 * mirrors gw-missions-api's OpenAPI `Product` schema
 * (`openapi/missions-api.yaml`) and `handler/products.go`'s `Product` Go
 * struct verbatim. Today the only real producer is the Subject Profile
 * assembler (PROD-027) — `sections` always carries exactly the
 * `SEC-051-subject-identification` section per `handler/products.go`'s
 * `AssembleSubjectProfile`.
 *
 * GW-11482: the OpenAPI spec's own `Product` schema description says this
 * is "written verbatim by the producing mission engine... byte-for-byte,
 * unparsed... PROD-CORE-004/006 are snake_case -- there is no single schema
 * that fits every product type." `productId` is therefore NOT guaranteed —
 * `product_id` is the real key for those two ids. Read via
 * `runProductId()`, never `product.productId` directly.
 */
export interface RunProduct {
  productId: string;
  /** Snake_case counterpart gw-missions-api sends for PROD-CORE-004/006. */
  product_id?: string;
  missionId: string;
  runId: string;
  ssdPath?: string;
  sections: RunProductSection[];
  meta: RunProductMeta;
}

/** GW-11482: resolve a list-response product's real id regardless of which
 * casing gw-missions-api sent for it (see `RunProduct`'s doc comment). */
export function runProductId(product: RunProduct): string {
  return product.productId ?? product.product_id ?? '';
}

/**
 * GET /v1/missions/{missionId}/runs/{runId}/products response — mirrors
 * gw-missions-api's OpenAPI `ProductListResponse` schema verbatim
 * (`required: [products]`). Lists every product JSON assembled so far for a
 * run, in one call — the run-results landing page's real data source.
 */
export interface ListRunProductsResponse {
  products: RunProduct[];
}

/**
 * GW-12838 (epic GW-12823): one recompute-and-compare result for a single
 * Merkle tree of a run's integrity products — mirrors gw-missions-api's
 * `MerkleTreeVerification` schema (openapi/missions-api.yaml, GW-12836)
 * verbatim. `name` identifies which tree: the audit trail
 * (PROD-CORE-003.merkleRoot), or the evidence register's collection
 * (PROD-CORE-002.merkle_root) or grading (grading_merkle_root) tree. The
 * endpoint emits every root under a single camelCase envelope
 * (`storedRoot`/`computedRoot`) regardless of the snake_case/camelCase spread
 * of the underlying source products — the casing reconciliation happens
 * producer-side, so consumers read one stable shape here.
 */
export interface MerkleTreeVerification {
  name: 'auditTrail' | 'evidenceCollection' | 'evidenceGrading';
  productId: 'PROD-CORE-002' | 'PROD-CORE-003';
  rootField: string;
  status: 'verified' | 'failed' | 'unavailable';
  /** The root the producer stamped into the product ("" when unavailable). */
  storedRoot: string;
  /** The root recomputed by the endpoint from the product's leaves. */
  computedRoot: string;
  leafCount: number;
  /** Human-readable context, present chiefly on unavailable trees. */
  detail?: string;
}

/**
 * GW-12838: one evidence-register entry projected to the camelCase
 * traceability contract — mirrors gw-missions-api's `TraceabilityEntry`
 * schema (GW-12836). Binds a collected record to the audit events that
 * produced it and the entity-graph nodes it populated.
 */
export interface TraceabilityEntry {
  id: string;
  queryId?: string;
  sourceDatasetId?: string;
  timestamp?: string;
  merkleLeafHash?: string;
  rawResponseHash?: string;
  auditTrailEntryIds: string[];
  entityGraphNodeIds: string[];
  admiraltyReliability?: string;
  /** Numeric or string as stored — passed through verbatim. */
  admiraltyCredibility?: number | string;
  /**
   * GW-12919: 0-based position of this entry's leaf in the
   * deterministically-ordered evidence-collection Merkle tree (same order as
   * the traceability array). The index of this entry's leaf within
   * `merkleProof`.
   */
  leafIndex?: number;
  /**
   * GW-12919: per-entry verification verdict for the evidence-collection tree
   * this entry belongs to. `verified` when the collection tree's recomputed
   * root equals its stored root (this entry's leaf is provably included via
   * `merkleProof`); `failed` on a collection-root mismatch (tamper/corruption);
   * `unavailable` when the register has no stored collection root. Mirrors the
   * collection `MerkleTreeVerification.status`.
   */
  verificationStatus?: 'verified' | 'failed' | 'unavailable';
  /**
   * GW-12919: evidence-collection Merkle inclusion proof for this entry's leaf.
   * Folding `merkleLeafHash` with these sibling nodes in order reproduces the
   * collection tree's `computedRoot`. Empty only for a single-leaf tree.
   */
  merkleProof?: EvidenceMerkleProofNode[];
}

/**
 * GW-12919: one step of an evidence-collection Merkle inclusion proof — the
 * sibling hash to combine with the running hash, and which side it sits on.
 * A verifier computes the parent as sha256_hex(siblingHash + running) when
 * `side` is `'left'`, or sha256_hex(running + siblingHash) when `side` is
 * `'right'`. Mirrors gw-missions-api's `EvidenceMerkleProofNode` schema.
 */
export interface EvidenceMerkleProofNode {
  /** The sibling node's SHA-256 hex digest at this proof level. */
  siblingHash: string;
  /** Which side of the pair the sibling occupies. */
  side: 'left' | 'right';
}

/**
 * GET /v1/missions/{missionId}/runs/{runId}/verification response — mirrors
 * gw-missions-api's `RunVerificationResult` schema (openapi/missions-api.yaml,
 * GW-12836) verbatim. Per-tree Merkle recompute-and-compare results plus the
 * evidence traceability view. `overallStatus` is `failed` if any tree failed,
 * `verified` if at least one tree verified and none failed, and `unavailable`
 * when no tree could be checked (nothing sealed for this run yet).
 */
export interface RunVerificationResult {
  missionId: string;
  runId: string;
  /** RFC 3339 UTC instant this verification was computed. */
  verifiedAt: string;
  overallStatus: 'verified' | 'failed' | 'unavailable';
  trees: MerkleTreeVerification[];
  traceability: TraceabilityEntry[];
}

/**
 * GW-12633 (PRP-PLAT-066 Task 7): `pendingDecision` on the Run GET response —
 * present when the run is paused awaiting a human decision at a named
 * decision point. Sourced from the authenticated run GET rather than from
 * notification payloads (PII constraint — candidate data must never flow
 * through notification bodies). The `decisionPoint` string is the human-
 * readable name (e.g. "subject-profile-disambiguation", "budget-approval"),
 * `decisionPointId` is the opaque server-assigned ID needed to submit the
 * response, and `payload` carries the decision-type-specific data (e.g.
 * candidate list for disambiguation, budget figures for budget-approval).
 */
export interface RunPendingDecision {
  decisionPoint: string;
  decisionPointId: string;
  payload: Record<string, unknown>;
  createdAt: string;
  /**
   * GW-13319 (W5): ISO-8601 deadline for the bounded-wait policy
   * (`expiresAt = now + GW_HITL_MAX_WAIT`, default 72h). Absent on
   * decisions that pre-date the W5 engine rollout. Surfaced to the UI
   * as a countdown in `HITLStatusBadge`.
   */
  expiresAt?: string;
}

/**
 * GW-9700: PUT /v1/missions/{missionId}/runs/{runId}/controls request body —
 * mirrors gw-missions-api's `RunControlEnvelope` schema
 * (openapi/missions-api.yaml) verbatim. GW-9698 removed the `/decisions`
 * routes this hook originally called (GW-9350/GW-9670) — they targeted a
 * resume mechanism (a dedicated SQS queue) gw-mission-engine never
 * provisions. The real, already-adopted resume path is this endpoint, which
 * already emits `mission.run.resumed` on EventBridge unconditionally and
 * (PR #206, commit 9e19ae8) carries `data.candidateId` specifically for a
 * human-driven HITL resume — see `RunControlResponse` below and
 * `useMissionRunHitlDecision.ts`'s header for the full reconciliation.
 *
 * `candidateId` is optional/nullable on the wire; the `none_match` choice
 * (gw-ui's "None Match" button) is submitted with it omitted — see this
 * issue's PR description for why that is an explicitly open product
 * question, not a client-side decision.
 *
 * GW-12634: `data.decisionPointId` + `data.response` are the generalized
 * decision payload for non-candidateId decision types (e.g. budget-approval).
 * The server returns 204 (no body) for the generalized path and 200 for the
 * backward-compat candidateId path.
 */
export interface RunControlEnvelope {
  event: 'mission.run.resume' | 'mission.run.pause' | 'mission.run.stop';
  data: {
    updated_budget?: number;
    candidateId?: string;
    decisionPointId?: string;
    response?: Record<string, unknown>;
  };
}

/**
 * GW-12589: optional body for POST /v1/missions/{missionId}/runs:create.
 * Supplying a client-generated `runId` enables idempotency — sending the
 * same key twice returns the existing Run record (200) instead of creating
 * a second run and a second SMART hold.
 */
export interface CreateRunRequest {
  runId: string;
}

/**
 * GW-9700: PUT .../controls response — mirrors gw-missions-api's
 * `RunControlResponse` schema verbatim.
 */
export interface RunControlResponse {
  accepted: boolean;
  holdId?: string;
  newTokenBudget?: number | null;
}

/**
 * GW-12251/GW-12252: one persisted batch of AG-UI activity for a run —
 * mirrors gw-missions-api's `RunActivityBatch` schema (GW-12251, PR #317,
 * `GET /v1/runs/{runId}/activity`) verbatim. Each batch is the exact
 * `{ events, sequence }` shape the live `mission.run.activity` websocket
 * payload carries (see `useMissionRunActivityEvents`), plus the server's
 * `emittedAt` stamp. `events` is the AG-UI item list forwarded byte-for-byte,
 * so it reuses `@gw/ui`'s `AgentActivityEvent` type — the same type
 * `AgentStreamCardSection` renders.
 */
export interface RunActivityBatch {
  sequence: number;
  events: AgentActivityEvent[];
  emittedAt: string;
}

/**
 * GET /v1/runs/{runId}/activity response — mirrors gw-missions-api's
 * `RunActivityResponse` schema (GW-12251). `activity` is ordered ascending by
 * `sequence`. Ownership is enforced as an empty result: an unauthorized or
 * unknown run returns 200 with `activity: []`, which the caller treats as "no
 * history" rather than an error (see `getRunActivity`).
 */
export interface RunActivityResponse {
  runId: string;
  activity: RunActivityBatch[];
  /**
   * GW-12681 (Epic GW-12680, Sentry GW-SPA-35): opaque pagination continuation
   * token. Pass it back as `getRunActivity`'s `cursor` param to fetch the next
   * page of batches — the NEWER page under `direction=forward` (the default),
   * or the OLDER page under `direction=backward` (GW-12728). `null` — or absent,
   * on a pre-pagination producer build that ignores the query params — means no
   * further pages remain in that direction. Mirrors the `nextCursor` convention
   * on `ListMissionsResponse` / `ListMarketplaceMissionsResponse`.
   */
  nextCursor?: string | null;
}

// ============================================================================
// Client
// ============================================================================

class MissionsDirectApiClient extends BaseApiClient {
  constructor() {
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    // GW-8262: suppressSessionExpired — this client fetches marketplace/mission
    // *resources* (e.g. a mission detail lookup by slug). A 401/404 here means
    // the resource lookup failed (bad slug, not-yet-authorized route, org
    // context not yet propagated), not that the user's session has expired.
    // The auth interceptor previously treated any 401 from this client as a
    // session-invalid signal and force-logged the user out of a page that
    // simply had a bad mission link. Genuine session expiry is still caught
    // by devStackAuth.util's getAuthToken() refresh-failure path, which emits
    // session-expired independently of this per-request 401 handling.
    super({
      baseURLFactory: () => getConfig().missionsApiBaseUrl ?? getConfig().apiBaseUrl,
      suppressSessionExpired: true,
    });
  }

  /**
   * List marketplace mission templates (public catalog).
   * GET /v1/marketplace/missions?limit=100
   * Returns up to 100 items; nextCursor indicates more exist.
   */
  async listMarketplaceMissions(): Promise<ListMarketplaceMissionsResponse> {
    return this.get<ListMarketplaceMissionsResponse>('/v1/marketplace/missions?limit=100');
  }

  /**
   * Get a single marketplace mission item by itemId (== slug == taxonomy id,
   * e.g. "OSINT-MISSION-01" — see docs/specs/public-marketplace-missions.md
   * in gw-missions-api).
   * GET /v1/marketplace/missions/{itemId}
   *
   * GW-8122: authenticated fallback for the mission detail page. Switches to
   * the public unauthenticated route (GET /v1/public/marketplace/missions/{itemId})
   * once gw-missions-api ships it — see the spec doc for the migration
   * sequence. Until then, this route requires a Cognito JWT.
   */
  async getMarketplaceMission(itemId: string): Promise<MarketplaceItem> {
    return this.get<MarketplaceItem>(`/v1/marketplace/missions/${itemId}`);
  }

  /**
   * List missions for the authenticated user, one server-side page at a time.
   * GET /v1/missions?limit={limit}&offset={offset}
   *
   * GW-10231: uses offset-based paging, not cursor — the two are mutually
   * exclusive per the deployed OpenAPI spec ("Zero-based page offset, for
   * UIs that need random-access page controls... Ordering is stable
   * (createdAt desc), so pages do not shift while a caller pages through
   * results"). `missionsSlice.fetchMissionsForScope` calls this in a loop
   * (offset 0, 20, 40, ...) until `total` is exhausted, then hands the FULL
   * assembled list to MissionListPage, which paginates it client-side via
   * `DataTablePagination` — this method itself has no knowledge of that.
   */
  async listMissions(params?: { limit?: number; offset?: number }): Promise<ListMissionsResponse> {
    const query = this.buildQueryString({
      ...(params?.limit !== undefined ? { limit: params.limit } : {}),
      ...(params?.offset !== undefined ? { offset: params.offset } : {}),
    });
    return this.get<ListMissionsResponse>(`/v1/missions${query}`);
  }

  /**
   * List runs for a single mission.
   * GET /v1/missions/{missionId}/runs
   */
  async listRuns(missionId: string): Promise<ListRunsResponse> {
    return this.get<ListRunsResponse>(`/v1/missions/${missionId}/runs`);
  }

  /**
   * Get a single run's poll-contract record.
   * GET /v1/missions/{missionId}/runs/{runId}
   *
   * GW-12633: the response may carry `pendingDecision` when the run is
   * paused awaiting a human decision. The base `Run` type from `@gw/ui` does
   * not yet carry this field (pending a gw-ui PR); the intersection here
   * bridges the gap without a local duplicate of the full Run shape.
   */
  async getRun(
    missionId: string,
    runId: string
  ): Promise<Run & { pendingDecision?: RunPendingDecision | null }> {
    return this.get<Run & { pendingDecision?: RunPendingDecision | null }>(
      `/v1/missions/${missionId}/runs/${runId}`
    );
  }

  /**
   * Get the real Mission record (prompt, target subject, breadth,
   * missionType, marketplaceTemplateId) for a single mission.
   * GET /v1/missions/{missionId}
   *
   * GW-8762: human-JWT-accessible endpoint that backs MissionRunPage's
   * header. Distinct from GET /v1/missions/{missionId}/briefing, which is
   * confirmed M2M-only (engine OAuth2 scope `missions/briefing.read`) and
   * is deliberately NOT called from gw-spa — a browser-issued call to that
   * route would 403.
   */
  async getMission(missionId: string): Promise<Mission> {
    return this.get<Mission>(`/v1/missions/${missionId}`);
  }

  /**
   * Fetch the rendered static map image for a subject's location marker.
   * GET /v1/maps/static — gw-missions-api's `cognito-jwt`-secured GetStaticMap
   * proxy (GW-12826).
   *
   * GW-12916 (follow-up of GW-12823 WS2): backs SEC-051's `LocationMiniMap`
   * (GW-12827). gw-ui is auth-agnostic, so this client owns transport — it
   * inherits the Cognito JWT + missions API base URL from `BaseApiClient` and
   * forwards the proxy's query params (`center`/`zoom`/`width`/`height`/`style`/
   * `colorScheme`) 1:1. A bare `<img src>` cannot carry the Authorization header
   * the proxy requires, so the image bytes are fetched and returned as a `Blob`
   * for gw-ui to object-URL. `signal` aborts the in-flight request when the
   * `LocationMiniMap` unmounts or its request params change.
   */
  async getStaticMap(request: StaticMapRequest, signal: AbortSignal): Promise<Blob> {
    const query = this.buildQueryString({
      center: request.center,
      zoom: request.zoom,
      width: request.width,
      height: request.height,
      style: request.style,
      colorScheme: request.colorScheme,
    });
    return this.getBlob(`/v1/maps/static${query}`, { signal });
  }

  /**
   * Get one assembled product's JSON for a run.
   * GET /v1/missions/{missionId}/runs/{runId}/products/{productId}
   *
   * GW-9864: confirmed against the deployed `handler/products.go`'s
   * `GetProduct` (via `functions/missions-products-get`) and this route's
   * OpenAPI entry — the real live path for PROD-027 (Subject Profile),
   * replacing the run-results slice's former `emptySubjectProfile()`
   * fallback. A 404 (product not yet assembled for this run) is a real,
   * expected response the caller maps to an honest-empty product, not an
   * error to retry indefinitely.
   */
  async getRunProduct(missionId: string, runId: string, productId: string): Promise<RunProduct> {
    return this.get<RunProduct>(`/v1/missions/${missionId}/runs/${runId}/products/${productId}`);
  }

  /**
   * List every product JSON assembled so far for a run, in one call.
   * GET /v1/missions/{missionId}/runs/{runId}/products
   *
   * GW-10160: `listRunProducts` (real, deployed per the OpenAPI spec's
   * `ProductListResponse`) — `RunResultsPage`'s real data source instead of
   * fetching each product id individually.
   */
  async listRunProducts(missionId: string, runId: string): Promise<ListRunProductsResponse> {
    return this.get<ListRunProductsResponse>(`/v1/missions/${missionId}/runs/${runId}/products`);
  }

  /**
   * GET /v1/missions/{missionId}/runs/{runId}/verification (GW-12838, epic
   * GW-12823) — the run's Merkle verification / traceability result
   * (gw-missions-api GW-12836). Recomputes the audit-trail, evidence-collection
   * and evidence-grading Merkle roots and compares each against its stored
   * value, so the run/product view can prove end-to-end tamper-evidence. Backs
   * the `MerkleVerification` display on `AuditTrailPage` via `useRunVerification`.
   */
  async getRunVerification(missionId: string, runId: string): Promise<RunVerificationResult> {
    return this.get<RunVerificationResult>(`/v1/missions/${missionId}/runs/${runId}/verification`);
  }

  /**
   * Fetch the persisted historical AG-UI activity for a run, in one call.
   * GET /v1/runs/{runId}/activity
   *
   * GW-12252 (Epic GW-12249): the REST backfill for
   * `useMissionRunActivityEvents`, which previously had NO history — a page
   * refresh mid-run lost every streamed activity card. Note the route is
   * top-level (`/v1/runs/{runId}`), keyed on runId ALONE, not nested under
   * `/v1/missions/{missionId}/...`. Backed by gw-missions-api GW-12251
   * (PR #317, merged). `activity` is ordered ascending by `sequence` and each
   * batch mirrors the live `mission.run.activity` websocket payload's
   * `{ events, sequence }` shape, so the hook seeds this on mount and then
   * merges live batches by sequence (dedupe on sequence). Ownership is
   * enforced as an empty result, not a 4xx: an unauthorized/unknown run
   * returns 200 with `activity: []`, so the caller treats empty as "no
   * history" rather than an error.
   *
   * GW-12681 (Epic GW-12680, Sentry GW-SPA-35): the route used to return EVERY
   * persisted batch (the full verbatim AG-UI transcript) in a single response —
   * flagged by Sentry GW-SPA-35 as a large HTTP payload (avg 240 KB, max
   * 410 KB, refetched on every pageload). It now accepts bounded pagination:
   * `limit` (max batches in the page) plus an opaque `cursor` (omitted on the
   * first request; pass back the prior response's `nextCursor` to walk the next
   * page). The response carries `nextCursor` (`string | null`).
   *
   * GW-12728 (Epic GW-12697): the route now also accepts a `direction` param
   * (`forward` | `backward`, default `forward`). With `direction=backward` the
   * cursorless first page is the NEWEST bounded window (descending by
   * `sequence`) and the opaque `cursor` walks toward OLDER batches; `nextCursor`
   * is `null` at the OLDEST batch. `useMissionRunActivityEvents` (GW-12699) uses
   * `backward` so a mounted run shows a bounded RECENT window immediately rather
   * than walking the whole forward chain, then fetches older pages on demand.
   * This IS the cross-repo contract the producer verifies against — see
   * `pact/consumers/mission-run-activity.pact.spec.ts`.
   */
  async getRunActivity(
    runId: string,
    params?: { limit?: number; cursor?: string; direction?: 'forward' | 'backward' }
  ): Promise<RunActivityResponse> {
    const query = this.buildQueryString({
      ...(params?.limit !== undefined ? { limit: params.limit } : {}),
      ...(params?.cursor !== undefined ? { cursor: params.cursor } : {}),
      ...(params?.direction !== undefined ? { direction: params.direction } : {}),
    });
    return this.get<RunActivityResponse>(`/v1/runs/${runId}/activity${query}`);
  }

  /**
   * Create a run for an existing mission.
   * POST /v1/missions/{missionId}/runs:create
   *
   * GW-8027: called immediately after mission creation so the user lands on
   * the run-detail page rather than an empty runs list. Returns 201 for a
   * new run or 200 on an idempotent retry — both carry the full `Run` record.
   *
   * GW-12589: accepts an optional `CreateRunRequest` body carrying a
   * client-generated `runId` idempotency key — generate it ONCE per user
   * gesture and reuse on any automatic retry so a double-click or network
   * retry hits the server's idempotent 200 path rather than creating a second
   * run and a second SMART hold.
   */
  async createRun(missionId: string, body?: CreateRunRequest): Promise<Run> {
    return this.post<Run>(`/v1/missions/${missionId}/runs:create`, body);
  }

  /**
   * Soft-delete a mission record.
   * DELETE /v1/missions/{missionId}
   *
   * GW-8633: best-effort cleanup for a mission that was created successfully
   * but whose paired run-creation call failed — see MissionCreatePage's
   * handleLaunch. Deliberately best-effort: this call's own failure is
   * swallowed by the caller (a mission left behind with no run is an
   * acceptable, recoverable outcome; blocking the user's error state on a
   * second network call is not).
   */
  async deleteMission(missionId: string): Promise<void> {
    return this.delete<void>(`/v1/missions/${missionId}`);
  }

  /**
   * Send a control message to a running mission run — resume (optionally
   * carrying a chosen HITL candidateId), pause, or stop.
   * PUT /v1/missions/{missionId}/runs/{runId}/controls
   *
   * GW-9700: replaces the removed GW-9350 `/decisions` routes. There is no
   * GET equivalent on this endpoint (fire-and-forget, not a query) — the
   * pending-decision candidate list comes from the `mission.run.hitl`
   * notification's own wire payload, not from this client. See
   * `useMissionRunHitlDecision.ts`'s header for the full reconciliation.
   */
  async submitRunControl(
    missionId: string,
    runId: string,
    envelope: RunControlEnvelope
  ): Promise<RunControlResponse> {
    return this.put<RunControlResponse>(
      `/v1/missions/${missionId}/runs/${runId}/controls`,
      envelope
    );
  }

  /**
   * GW-11455 (PRP-MKTPL-016 FR-6, FR-23): check the current user's/org's TOS
   * acceptance status for a mission catalog item (the marketplace template
   * `itemId` — same identifier `getMarketplaceMission`/`listMarketplaceMissions`
   * use, not a runtime mission-run id). Uniform route shape shared across all
   * four item types once gw-missions-api ships it (GW-11450, sibling issue);
   * calling this against today's deployed API will 404 until that lands.
   * GET /v1/missions/{itemId}/tos/acceptance
   */
  async checkMissionTOSAcceptance(itemId: string): Promise<MissionTOSAcceptanceStatus> {
    return this.get<MissionTOSAcceptanceStatus>(`/v1/missions/${itemId}/tos/acceptance`);
  }

  /**
   * Accept TOS (user-level) for a mission catalog item.
   * POST /v1/missions/{itemId}/tos/accept/user
   */
  async acceptMissionTOSUser(
    itemId: string,
    data: AcceptMissionTOSRequest
  ): Promise<MissionTOSAcceptance> {
    return this.post<MissionTOSAcceptance>(`/v1/missions/${itemId}/tos/accept/user`, data);
  }

  /**
   * Accept TOS (org-level) for a mission catalog item. Backend gate
   * (FR-17) requires the caller be org_admin/global_admin — this client
   * call itself performs no authorization; a non-admin caller gets a 403
   * from the server.
   * POST /v1/missions/{itemId}/tos/accept/org
   */
  async acceptMissionTOSOrg(
    itemId: string,
    data: AcceptMissionTOSRequest
  ): Promise<MissionTOSAcceptance> {
    return this.post<MissionTOSAcceptance>(`/v1/missions/${itemId}/tos/accept/org`, data);
  }

  /**
   * GW-11493: FR-29 cross-mission org TOS overview (org_admin/global_admin only).
   * GET /v1/missions/tos/org-overview
   */
  async getMissionsOrgTOSOverview(): Promise<MissionOrgTOSOverviewResponse> {
    return this.get<MissionOrgTOSOverviewResponse>('/v1/missions/tos/org-overview');
  }
}

export const missionsDirectApi = new MissionsDirectApiClient();
